import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiClient } from '../api/client'
import type { Study } from '../types'
import EmptyState from './ui/EmptyState'
import { FileText, Plus, ClipboardList, LoaderCircle, AlertCircle } from 'lucide-react'
import { useAuthStore } from '../stores/authStore'

const SORT_FIELDS = [
  { value: 'created_at', label: 'Created' },
  { value: 'study_name', label: 'Name' },
  { value: 'study_code', label: 'Code' },
  { value: 'phase', label: 'Phase' },
  { value: 'updated_at', label: 'Updated' },
] as const

type SortField = (typeof SORT_FIELDS)[number]['value']

interface StudyFormData {
  study_name: string
}

const EMPTY_FORM: StudyFormData = {
  study_name: '',
}

function StudiesPage() {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const canCreateOrUpload = user?.role !== 'Viewer'
  const [studies, setStudies] = useState<Study[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [sortField, setSortField] = useState<SortField>('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [modalOpen, setModalOpen] = useState(false)
  const [editingStudy, setEditingStudy] = useState<Study | null>(null)
  const [form, setForm] = useState<StudyFormData>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [uploadingStudyId, setUploadingStudyId] = useState<string | null>(null)

  const fetchStudies = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    setError(null)
    try {
      const params: Record<string, string> = {
        sort_field: sortField,
        sort_order: sortOrder,
      }
      if (search.trim()) params.search = search.trim()
      const data = await apiClient.get<{ items: Study[] }>('/studies', { params })
      setStudies(data.items)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load studies'
      setError(msg)
    } finally {
      if (!silent) setLoading(false)
    }
  }, [search, sortField, sortOrder])

  useEffect(() => {
    fetchStudies()
  }, [fetchStudies])

  useEffect(() => {
    if (!studies.some((study) => study.protocol_processing_status && study.protocol_processing_status !== 'failed')) return
    const timer = window.setInterval(() => { void fetchStudies(true) }, 3000)
    return () => window.clearInterval(timer)
  }, [studies, fetchStudies])

  const openCreate = () => {
    setEditingStudy(null)
    setForm(EMPTY_FORM)
    setModalOpen(true)
  }

  const openEdit = (study: Study) => {
    setEditingStudy(study)
    setForm({
      study_name: study.study_name,
    })
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setEditingStudy(null)
    setForm(EMPTY_FORM)
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.study_name.trim()) {
      setError('Study name is required')
      return
    }
    setSaving(true)
    setError(null)
    try {
      if (editingStudy) {
        await apiClient.put<Study>(`/studies/${editingStudy.id}`, form)
      } else {
        await apiClient.post<Study>('/studies', form)
      }
      await fetchStudies()
      closeModal()
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save study'
      setError(msg)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (studyId: string) => {
    if (!window.confirm('Are you sure you want to delete this study? This action cannot be undone.')) {
      return
    }
    setDeletingId(studyId)
    try {
      await apiClient.delete(`/studies/${studyId}`)
      setStudies((prev: Study[]) => prev.filter((s: Study) => s.id !== studyId))
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to delete study'
      setError(msg)
    } finally {
      setDeletingId(null)
    }
  }

  const handleUploadIntoStudy = async (studyId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setError('Only PDF files are supported')
      return
    }
    setUploadingStudyId(studyId)
    setError(null)
    try {
      const upload = await apiClient.uploadFile<{
        id: string
        job_id?: string
        status?: string
        message?: string
      }>('/protocols', file, undefined, undefined, {
        study_id: studyId,
        on_conflict: 'replace',
        auto_extract: 'false',
      })
      setUploadingStudyId(null)
      await fetchStudies(true)
      if (upload.id) {
        await apiClient.get(`/protocols/${upload.id}/deterministic-preview`, { timeout: 0 })
      }
      await fetchStudies(true)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to upload protocol'
      setError(msg)
      setUploadingStudyId(null)
    } finally {
      if (e.target) e.target.value = ''
    }
  }

  const modalInputClass =
    'mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-500 bg-gray-50 dark:bg-gray-900/70 text-gray-900 dark:text-white px-3.5 py-2.5 shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none transition-colors'

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Studies</h2>
        <div className="flex items-center gap-3">
          <button
            onClick={() => { void fetchStudies() }}
            disabled={loading}
            className="text-sm px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg transition-colors disabled:opacity-50"
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
          {canCreateOrUpload && <button onClick={openCreate} className="btn-primary">
            <Plus className="h-4 w-4 mr-1.5" aria-hidden={true} />
            Create Study
          </button>}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="text"
          placeholder="Search studies..."
          value={search}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
          className="flex-1 rounded-lg border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 focus:border-primary-500 focus:ring-primary-500"
        />
        <div className="flex items-center gap-2">
          <select
            value={sortField}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSortField(e.target.value as SortField)}
            className="rounded-lg border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 focus:border-primary-500 focus:ring-primary-500"
          >
            {SORT_FIELDS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
          <button
            onClick={() => setSortOrder((o: 'asc' | 'desc') => (o === 'asc' ? 'desc' : 'asc'))}
            className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600"
            aria-label="Toggle sort order"
          >
            {sortOrder === 'asc' ? '↑' : '↓'}
          </button>
        </div>
      </div>

      {error && !modalOpen && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-lg">
          {error}
        </div>
      )}

      {studies.length === 0 ? (
        <EmptyState
          icon={FileText}
          title={search ? 'No studies match your search' : 'No studies found'}
          description={canCreateOrUpload ? "Create a study, then upload a protocol into it." : "No studies are available to your account yet. Ask an administrator to grant access."}
          action={
            canCreateOrUpload ? <button onClick={openCreate} className="btn-primary">
              <Plus className="h-4 w-4 mr-1.5" aria-hidden={true} />
              Create Study
            </button> : undefined
          }
        />
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Phase
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Protocol processing
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {studies.map((study) => (
                <tr
                  key={study.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                >
                  <td
                    onClick={() => navigate(`/studies/${study.id}`)}
                    className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white cursor-pointer"
                  >
                    {study.study_name}
                  </td>
                  <td
                    onClick={() => navigate(`/studies/${study.id}`)}
                    className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 cursor-pointer"
                  >
                    {study.study_code || '—'}
                  </td>
                  <td
                    onClick={() => navigate(`/studies/${study.id}`)}
                    className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 cursor-pointer"
                  >
                    {study.phase || '—'}
                  </td>
                  <td
                    onClick={() => navigate(`/studies/${study.id}`)}
                    className="px-6 py-4 whitespace-nowrap cursor-pointer"
                  >
                    <div className="space-y-1">
                      {study.protocol_processing_status && study.protocol_processing_status !== 'failed' && (
                        <div className="flex items-center gap-1.5 text-xs font-medium text-primary-700 dark:text-primary-300" role="status" aria-live="polite">
                          <LoaderCircle className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                          <span>{study.protocol_processing_stage || (study.protocol_processing_status === 'ai_extraction' ? 'Extracting protocol' : 'Processing protocol')}</span>
                        </div>
                      )}
                      {study.protocol_processing_status === 'failed' && (
                        <div className="flex items-center gap-1.5 text-xs text-red-700 dark:text-red-300" role="status">
                          <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
                          <span>Protocol processing failed</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm space-x-3">
                    {canCreateOrUpload && <label className={`inline-flex items-center font-medium ${study.protocol_processing_status && study.protocol_processing_status !== 'failed' ? 'cursor-not-allowed text-gray-400' : 'cursor-pointer text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300'}`}>
                      {uploadingStudyId === study.id ? 'Uploading…' : study.protocol_processing_status && study.protocol_processing_status !== 'failed' ? 'Processing…' : 'Upload Protocol'}
                      <input
                        type="file"
                        accept=".pdf"
                        className="hidden"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleUploadIntoStudy(study.id, e)}
                        disabled={uploadingStudyId === study.id || Boolean(study.protocol_processing_status && study.protocol_processing_status !== 'failed')}
                      />
                    </label>}
                    <button
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation()
                        openEdit(study)
                      }}
                      className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300 font-medium"
                    >
                      Edit
                    </button>
                    <button
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation()
                        handleDelete(study.id)
                      }}
                      disabled={deletingId === study.id}
                      className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 font-medium disabled:opacity-50 transition-colors"
                    >
                      {deletingId === study.id ? 'Deleting…' : 'Delete'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden border border-gray-200 dark:border-gray-700">
            <div className="bg-gray-50/80 dark:bg-gray-900/50 px-6 py-5 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                  <ClipboardList className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {editingStudy ? 'Edit Study' : 'Create Study'}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {editingStudy ? 'Update the study name.' : 'Enter a name to create a shared study workspace.'}
                  </p>
                </div>
              </div>
              <button
                onClick={closeModal}
                className="mt-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg p-1 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="p-6 space-y-5">
              {error && modalOpen && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-lg text-sm">
                  {error}
                </div>
              )}
              <form onSubmit={handleSave} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200">
                    Study Name
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 mb-1.5">
                    The display name for this study.
                  </p>
                  <input
                    type="text"
                    required
                    value={form.study_name}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((prev: StudyFormData) => ({ ...prev, study_name: e.target.value }))}
                    className={modalInputClass}
                    placeholder="e.g., Phase II Diabetes Study"
                  />
                </div>
                <div className="flex items-center justify-end gap-3 pt-5 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white rounded-lg disabled:opacity-50 transition-colors"
                  >
                    {saving ? 'Saving…' : editingStudy ? 'Save Changes' : 'Create Study'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default StudiesPage
